import { Bubble } from '@landing-blocks/shared/components/bubble/bubble.js';
import { Button } from '@landing-blocks/shared/components/button/button.js';
import { HtmlText } from '@landing-blocks/shared/components/html-text/html-text.js';
import type { FunctionalComponent } from 'preact';

import { BlockBubbledCard1Body as Body } from './__body/block-bubbled-card-1__body.js';
import { BlockBubbledCard1Content as Content } from './__content/block-bubbled-card-1__content.js';
import { BlockBubbledCard1Footer as Footer } from './__footer/block-bubbled-card-1__footer.js';
import { BlockBubbledCard1Heading as Heading } from './__heading/block-bubbled-card-1__heading.js';
import { BlockBubbledCard1ListItem as ListItem } from './__list-item/block-bubbled-card-1__list-item.js';
import { BlockBubbledCard1List as List } from './__list/block-bubbled-card-1__list.js';
import { BlockBubbledCard1Title as Title } from './__title/block-bubbled-card-1__title.js';
import { cnBlockBubbledCard1 } from './block-bubbled-card-1.constants.js';

import './block-bubbled-card-1.css';

import './__button/block-bubbled-card-1__button.css';
import './__first-bubble/block-bubbled-card-1__first-bubble.css';
import './__pre-bubble-content/block-bubbled-card-1__pre-bubble-content.css';
import './__second-bubble/block-bubbled-card-1__second-bubble.css';

type BlockAttributes = {
    title: string;
    firstBubbleBackgroundColor?: string;
    firstBubbleText?: string;

    secondBubbleBackgroundColor?: string;
    secondBubbleText?: string;

    paragraphText?: string;

    firstListItemText?: string;
    secondListItemText?: string;
    thirdListItemText?: string;

    buttonText?: string;
    buttonLink?: string;

    cohortDate: string;
    cohortDateShown?: string;

    nextGroupStartsSoonText: string;
    joinBeforeStartDateText: string;
};

export interface BlockBubbledCard1Props extends BlockAttributes {}

export const BlockBubbledCard1: FunctionalComponent<BlockBubbledCard1Props> = ({
    title,

    firstBubbleBackgroundColor,
    firstBubbleText,

    secondBubbleBackgroundColor,
    secondBubbleText,

    paragraphText,

    firstListItemText,
    secondListItemText,
    thirdListItemText,

    buttonText,
    buttonLink,

    cohortDate,
    joinBeforeStartDateText,
    nextGroupStartsSoonText,
    cohortDateShown,
}) => {
    const isListVisible = Boolean(firstListItemText || secondListItemText || thirdListItemText);
    const isButtonVisible = !!buttonText && !!buttonLink;

    const isFooterVisible = Boolean(isListVisible || isButtonVisible);
    const isLessBodyWidth = Boolean(secondBubbleText && paragraphText);
    const isLessListWidth = Boolean(isListVisible && secondBubbleText && !paragraphText);

    return (
        <div className={cnBlockBubbledCard1({})}>
            <Heading>
                {firstBubbleText && (
                    <Bubble
                        className={cnBlockBubbledCard1('first-bubble')}
                        backgroundColor={firstBubbleBackgroundColor}
                        tailHorizontalPosition="left"
                        tailVerticalPosition="bottom"
                    >
                        <HtmlText html={firstBubbleText} />
                    </Bubble>
                )}
                <Title>
                    <HtmlText html={title} />
                </Title>
                {secondBubbleText && (
                    <Bubble
                        className={cnBlockBubbledCard1('second-bubble')}
                        backgroundColor={secondBubbleBackgroundColor}
                        tailHorizontalPosition="left"
                        tailVerticalPosition="top"
                    >
                        <HtmlText html={secondBubbleText} />
                    </Bubble>
                )}
            </Heading>
            <Content>
                {Boolean(paragraphText) && (
                    <Body className={isLessBodyWidth ? cnBlockBubbledCard1('pre-bubble-content') : undefined}>
                        {Boolean(paragraphText) && (
                            <p>
                                <HtmlText html={paragraphText} />
                            </p>
                        )}
                        {cohortDateShown === 'true' && (
                            <p>{cohortDate ? `${joinBeforeStartDateText} ${cohortDate}` : nextGroupStartsSoonText}</p>
                        )}
                    </Body>
                )}
                {isFooterVisible && (
                    <Footer>
                        {isListVisible && (
                            <List className={isLessListWidth ? cnBlockBubbledCard1('pre-bubble-content') : undefined}>
                                {firstListItemText && (
                                    <ListItem>
                                        <HtmlText html={firstListItemText} />
                                    </ListItem>
                                )}
                                {secondListItemText && (
                                    <ListItem>
                                        <HtmlText html={secondListItemText} />
                                    </ListItem>
                                )}
                                {thirdListItemText && (
                                    <ListItem>
                                        <HtmlText html={thirdListItemText} />
                                    </ListItem>
                                )}
                            </List>
                        )}
                        {isButtonVisible && (
                            <Button className={cnBlockBubbledCard1('button')} href={buttonLink} mode="dark">
                                {buttonText}
                            </Button>
                        )}
                    </Footer>
                )}
            </Content>
        </div>
    );
};
